import { create } from 'zustand';

import { Appointment } from '@src/components/UserPortal/UserPortal.types';
import { mountDevTools } from '@src/store/utils/mount-dev-tools';

export type State = {
	accessed: boolean;
	appointmentDialogOpen: boolean;
	focusedAppointment: Appointment | null;
	editInformationDialogOpen: boolean;
	editPrimaryHospitalDialogOpen: boolean;
	petInformationDialogOpen: boolean;
	toastNotification: {
		show: boolean;
		message?: string;
		severity?: string;
		variant?: string;
	};
	userCoordinates?: { latitude: number; longitude: number } | null;
	slotTakenDialogOpen: boolean;
	secondaryOwnerDialogOpen: boolean;
	relationshipId?: string | null;
	memorializedPetsDialogOpen: boolean;
	visitSummaryMessage?: { title: string; message: string } | null;
	dischargeInstructionDialogOpen: boolean;
	clientRegistrationFormId: string | null;
	clientRegistrationFormName: string | null;
};

type Actions = {
	setAccessed: (accessed: boolean) => void;
	setAppointmentDialogOpen: (open: boolean) => void;
	setFocusedAppointment: (appointment: Appointment | null) => void;
	setEditInformationDialogOpen: (open: boolean) => void;
	setEditPrimaryHospitalDialogOpen: (open: boolean) => void;
	setPetInformationDialogOpen: (open: boolean) => void;
	setToastNotification: (show: boolean, message?: string, severity?: string, variant?: string) => void;
	setUserCoordinates: (coords: { latitude: number; longitude: number } | null) => void;
	setSlotTakenDialogOpen: (open: boolean) => void;
	setSecondaryOwnerDialogOpen: (open: boolean) => void;
	setRelationshipId: (id: string | null) => void;
	setMemorializedPetsDialogOpen: (open: boolean) => void;
	setVisitSummaryMessage: (visitSummaryMessage: { title: string; message: string }) => void;
	setDischargeInstructionDialogOpen: (open: boolean) => void;
	setClientRegistrationFormId: (id: string | null) => void;
	setClientRegistrationFormName: (name: string | null) => void;
};

const INITIAL_STATE: State = {
	accessed: false,
	appointmentDialogOpen: false,
	focusedAppointment: null,
	editInformationDialogOpen: false,
	editPrimaryHospitalDialogOpen: false,
	petInformationDialogOpen: false,
	toastNotification: { show: false },
	userCoordinates: null,
	slotTakenDialogOpen: false,
	secondaryOwnerDialogOpen: false,
	relationshipId: null,
	memorializedPetsDialogOpen: false,
	visitSummaryMessage: undefined,
	dischargeInstructionDialogOpen: false,
	clientRegistrationFormId: null,
	clientRegistrationFormName: null,
};

const useUserPortalStore = create<State & Actions>((set) => ({
	...INITIAL_STATE,
	setAccessed: (accessed) => set({ accessed }),
	setAppointmentDialogOpen: (open) => set({ appointmentDialogOpen: open }),
	setFocusedAppointment: (focusedAppointment) => set({ focusedAppointment }),
	setEditInformationDialogOpen: (open) => set({ editInformationDialogOpen: open }),
	setEditPrimaryHospitalDialogOpen: (open) => set({ editPrimaryHospitalDialogOpen: open }),
	setPetInformationDialogOpen: (open) => set({ petInformationDialogOpen: open }),
	setToastNotification: (show, message, severity, variant) =>
		set({ toastNotification: { show, message, severity, variant } }),
	setUserCoordinates: (coords) => set({ userCoordinates: coords }),
	setSlotTakenDialogOpen: (open) => set({ slotTakenDialogOpen: open }),
	setSecondaryOwnerDialogOpen: (open) => set({ secondaryOwnerDialogOpen: open }),
	setRelationshipId: (id) => set({ relationshipId: id }),
	setMemorializedPetsDialogOpen: (open) => set({ memorializedPetsDialogOpen: open }),
	setVisitSummaryMessage: (visitSummaryMessage) => set({ visitSummaryMessage }),
	setDischargeInstructionDialogOpen: (open) => set({ dischargeInstructionDialogOpen: open }),
	setClientRegistrationFormId: (id) => set({ clientRegistrationFormId: id }),
	setClientRegistrationFormName: (name) => set({ clientRegistrationFormName: name }),
}));

mountDevTools('useUserPortalStore', useUserPortalStore);

export default useUserPortalStore;
